import { useAppDispatch, useAppSelector } from 'store/hooks';
import { Box, Button, Paper } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { AdjustmentScheme } from 'components/adjustment-schemes/adjustmentSchemeModel';
import { adjustmentSchemesApi } from 'components/adjustment-schemes/adjustmentSchemesApi';
import { SubscriptionScheme } from 'components/subscription-schemes/subscriptionSchemeModel';
import { subscriptionSchemesApi } from 'components/subscription-schemes/subscriptionSchemesApi';
import { AudienceEditTimeInputCell } from 'components/audience/grid/AudienceEditTimeInput';
import { useAudiences } from 'components/audience/hooks/useAudiences';
import { AudienceCreationStepper } from 'components/audience/modals/AudienceCreationStepper';
import { getMetricFormatting } from 'components/audience/models/metrics';
import { AudienceType } from 'components/audience/models/segmentModel';
import { EmptyState } from 'components/utils/emptyState';

import { MARKETING_AUDIENCES_VIEW } from 'config/routes';
import { MainLayout } from 'layouts/MainLayout';
import { useLeavePageBlock } from 'lib/hooks/useLeavePageBlock';
import { useSimpleModalState } from 'lib/hooks/useSimpleModalState';
import logger from 'lib/logger';
import { defaultDateTimeValueFormatter } from 'lib/MuiGrid/helpers';
import { LinkIdCell, LinkIdCellRowModel } from 'lib/MuiGrid/LinkIdCell';
import { EMuiGridStatus, GridStateHelper, MuiGrid } from 'lib/MuiGrid/MuiGrid';
import { GridStorageName } from 'lib/MuiGrid/StateController';
import { useGridSearch } from 'lib/MuiGrid/useGridSearch';
import { getSecondsToDaysHours } from 'lib/timeHelpers';
import { isDefined, isNumber } from 'lib/typeguards';
import React from 'react';
import EmptyImage from 'static/audience-empty-state.svg';
import { updateAudience } from 'store/audiences/audiencesSlice';
import { getAllLocations } from 'store/locations/locationsSlice';
import { useSnackbar } from 'notistack';
import { getAllPerks } from 'store/perks/perksSlice';
import { Row } from 'ui/Flex';
import * as Yup from 'yup';

const emptyStateDescription =
    'Audiences unlock new ways to target marketing content at your customer base by grouping them based on their habits within the app.';

function expiryValueFormatter(value: number | string) {
    return isNumber(value) ? getSecondsToDaysHours(value) : '-';
}

const validationScheme = {
    title: Yup.string().required('Title is required.')
};

export function useAudienceChange() {
    const gridRef = React.useRef<GridStateHelper>(null);
    const [readyToSave, setReadyToSave] = React.useState(false);
    const handleCancel = React.useCallback(() => {
        if (gridRef.current) {
            gridRef.current.reset();
        }
    }, []);
    const handleStatusChange = React.useCallback(
        (status: EMuiGridStatus) => {
            if (readyToSave && status === EMuiGridStatus.DEFAULT) {
                setReadyToSave(false);
            }
            if (!readyToSave && status === EMuiGridStatus.CHANGED) {
                setReadyToSave(true);
            }
        },
        [readyToSave]
    );
    return { gridRef, readyToSave, handleCancel, handleStatusChange };
}

export const AudiencesPage: React.FC = () => {
    const { loading, presentMetrics, audiences } = useAudiences();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [addAudienceOpen, openModal, closeModal] = useSimpleModalState();

    const { locations } = useAppSelector(state => state.locations);
    const { perks } = useAppSelector(state => state.perks);
    const [adjustmentSchemes, setAdjustmentSchemes] = React.useState<AdjustmentScheme[]>([]);
    const [subscriptionSchemes, setSubscriptionSchemes] = React.useState<SubscriptionScheme[]>([]);
    const { gridRef, readyToSave, handleCancel, handleStatusChange } = useAudienceChange();

    React.useEffect(() => {
        dispatch(getAllPerks());
    }, [dispatch]);

    React.useEffect(() => {
        dispatch(getAllLocations());
    }, [dispatch]);

    React.useEffect(() => {
        async function fetchAdjustmentSchemes() {
            try {
                const response = await adjustmentSchemesApi.getAllSchemes();
                if (response.ok) {
                    setAdjustmentSchemes(response.items);
                }
            } catch (err) {
                logger.error(err);
            }
        }
        fetchAdjustmentSchemes();
    }, []);

    React.useEffect(() => {
        async function fetchSubscriptionSchemes() {
            try {
                const response = await subscriptionSchemesApi.getList();
                if (response.ok && response.body?.items) {
                    setSubscriptionSchemes(response.body.items);
                }
            } catch (err) {
                logger.error(err);
            }
        }
        fetchSubscriptionSchemes();
    }, []);
    const renderViewCell = React.useCallback(
        (params: GridCellParams<LinkIdCellRowModel, string>) => (
            <LinkIdCell id={params.value} param="audienceId" path={MARKETING_AUDIENCES_VIEW} />
        ),
        []
    );
    useLeavePageBlock(readyToSave);
    const columns = React.useMemo<GridColDef[]>(() => {
        const permanentColumns: GridColDef[] = [
            {
                headerName: 'Audience ID',
                field: 'id',
                disableReorder: true,
                disableExport: true,
                sortable: false,
                width: 240,
                renderCell: renderViewCell
            },
            {
                headerName: 'Title',
                field: 'title',
                width: 240,
                editable: true
            },
            {
                headerName: 'Description',
                field: 'description',
                width: 200,
                editable: true
            },
            {
                headerName: 'Type',
                field: 'type',
                width: 150
            },
            {
                headerName: 'Customers',
                field: 'count',
                align: 'right',
                headerAlign: 'left',
                width: 150
            },
            {
                headerName: 'Expiry Time',
                align: 'right',
                headerAlign: 'left',
                field: 'expiryHorizon',
                width: 160,
                editable: true,
                valueFormatter: expiryValueFormatter,
                renderEditCell: AudienceEditTimeInputCell
            },
            {
                headerName: 'Created',
                field: 'updated',
                width: 180,
                type: 'dateTime',
                valueFormatter: defaultDateTimeValueFormatter
            }
        ];
        if (presentMetrics.length > 0) {
            presentMetrics.forEach(item => {
                const { title, valueFormatter } = getMetricFormatting(item);
                permanentColumns.push({
                    headerName: title,
                    align: 'right',
                    headerAlign: 'left',
                    type: 'number',
                    field: item,
                    width: 250,
                    valueFormatter
                });
            });
        }
        return permanentColumns;
    }, [presentMetrics, renderViewCell]);

    const handleEmptyStateClick = React.useCallback(() => {
        openModal();
    }, [openModal]);

    const handleSubmit = React.useCallback(async () => {
        try {
            if (!gridRef.current) {
                return;
            }
            const errors = gridRef.current.validate();
            if (errors) {
                return;
            }
            const { changes } = gridRef.current.getState();
            const [[id, data]] = Object.entries(changes);
            await dispatch(updateAudience(id, data));
            gridRef.current.reset();
        } catch (e) {
            logger.error(e);
            enqueueSnackbar(e.message, { variant: 'error' });
        }
    }, [gridRef, dispatch, enqueueSnackbar]);

    const gridSearchOptions = React.useMemo(
        () => [{ column: 'id' }, { column: 'title' }, { column: 'description' }, { column: 'type' }],
        []
    );
    const { filterModel, renderInput } = useGridSearch(
        gridSearchOptions,
        'Search by ID, title, description or type'
    );
    const isCellEditable = React.useCallback(
        (params: GridCellParams<any, any, any>) => {
            if (gridRef.current) {
                const { changes } = gridRef.current.getState();
                const [id] = Object.keys(changes);
                if (
                    params.colDef.editable &&
                    (params.colDef.field !== 'expiryHorizon' ||
                        params.row.type !== AudienceType.PERSISTENT) &&
                    (!isDefined(id) || params.id === id)
                ) {
                    return true;
                }
            }
            return false;
        },
        [gridRef]
    );
    const handleCellDoubleClick = React.useCallback(
        (params: GridCellParams<any, any, any>) => {
            if (gridRef.current) {
                const { changes } = gridRef.current.getState();
                const [id] = Object.keys(changes);
                if (isCellEditable(params) && isDefined(id) && params.id !== id) {
                    enqueueSnackbar('Cannot edit more than ONE audience at a time', {
                        variant: 'warning'
                    });
                }
            }
        },
        [gridRef, isCellEditable, enqueueSnackbar]
    );
    return (
        <MainLayout
            pageName="Audiences"
            pageDescription="Group users together based on their activity."
            noScroll
        >
            {!loading && (!Array.isArray(audiences) || audiences.length === 0) ? (
                <EmptyState
                    headerText="Group your customers into audiences"
                    paragraphText={emptyStateDescription}
                    buttonText="Create an audience"
                    imageUrl={EmptyImage}
                    onClick={handleEmptyStateClick}
                />
            ) : (
                <>
                    <Box paddingY={1}>
                        <Row align="space-between">
                            {renderInput()}
                            <Row gutter valign="center">
                                {readyToSave && (
                                    <React.Fragment>
                                        <Box>
                                            <Button size="small" onClick={handleCancel}>
                                                Cancel
                                            </Button>
                                        </Box>
                                        <Box>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disabled={loading}
                                                onClick={handleSubmit}
                                            >
                                                Save
                                            </Button>
                                        </Box>
                                    </React.Fragment>
                                )}
                                <Box>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={openModal}
                                        disabled={loading || readyToSave}
                                    >
                                        Create audience
                                    </Button>
                                </Box>
                            </Row>
                        </Row>
                    </Box>
                    <Paper sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
                        <MuiGrid
                            sx={{
                                borderRadius: 0,
                                borderBottom: 'none',
                                borderLeft: 'none',
                                borderRight: 'none'
                            }}
                            rows={audiences}
                            columns={columns}
                            hideFooter={false}
                            pagination
                            loading={loading}
                            pageSizeOptions={[100]}
                            filterModel={filterModel}
                            validationScheme={validationScheme}
                            disableColumnFilter
                            stateRef={gridRef}
                            isCellEditable={isCellEditable}
                            storageName={GridStorageName.Audiences}
                            onStatusChange={handleStatusChange}
                            onCellDoubleClick={handleCellDoubleClick}
                        />
                    </Paper>
                </>
            )}
            <AudienceCreationStepper
                open={addAudienceOpen}
                onClose={closeModal}
                locations={locations}
                perks={perks}
                adjustmentSchemes={adjustmentSchemes}
                subscriptionSchemes={subscriptionSchemes}
                audiences={audiences}
            />
        </MainLayout>
    );
};
